import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { isAbortedNetworkError } from "shared/src/utils/apollo-error-utils";

const sentryEnv = import.meta.env.VITE_SENTRY_ENV ?? "local";

if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
  Sentry.init({
    dsn: "https://63ed755eaf0796bcd56e6e885368f2f6@o4507902607818752.ingest.us.sentry.io/4507903019057152",
    environment: sentryEnv,
    beforeSend(event, hint) {
      // Drops AbortErrors that surface as unhandled promise rejections — e.g.
      // a useLazyQuery.execute() call that aborts the prior in-flight one, or
      // a useQuery whose component unmounts mid-request. These bypass
      // apolloErrorLink because they never flow through the link chain; they
      // hit Sentry via the global unhandledrejection handler instead.
      const original = hint?.originalException;
      if (original instanceof Error && isAbortedNetworkError(original)) {
        return null;
      }
      return event;
    },
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["claimable-ui"],
        behaviour: "drop-error-if-contains-third-party-frames",
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
        mask: ["input", "textarea", "select", "[data-sentry-mask]"],
        block: ["[data-sentry-block]"],
        ignore: ["[data-sentry-ignore]"],
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.captureConsoleIntegration(),
      Sentry.extraErrorDataIntegration(),
    ],
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      /^https:\/\/.*\.execute-api\.us-east-1\.amazonaws\.com/,
      /^https:\/\/.*\.preclaimable\.com/,
    ],
  });
}
